/**
 * Final CTA Section
 * Strong call-to-action with gradient background
 */

import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { itemVariants } from '@/components/animations/variants';
import { useNavigate } from 'react-router-dom';

const benefits = [
  'Usage-based billing',
  'Transparent platform fee',
  'Cancel anytime',
];

export function CtaSection() {
  const navigate = useNavigate();

  const handleDemoClick = () => {
    navigate('/demo');
  };

  const handleTalkToSalesClick = () => {
    navigate('/contact');
  };

  return (
    <section
      id="contact"
      className="relative py-16 px-4 text-white"
      style={{ backgroundColor: 'hsl(194, 92%, 18%)' }}
    >
      {/* Orange highlight bar at top */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: 'hsl(15, 100%, 56%)' }}
      />
      <motion.div
        className="max-w-4xl mx-auto text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.2,
              delayChildren: 0.1,
            },
          },
        }}
      >
        {/* Heading */}
        <motion.h2
          className="text-4xl md:text-5xl font-bold mb-6"
          variants={itemVariants}
        >
          Start Optimizing Your AI Costs Today
        </motion.h2>

        {/* Subtitle */}
        <motion.p className="text-xl mb-8 opacity-90" variants={itemVariants}>
          Join developers who are saving 30-70% on AI API costs
          <br />
          without sacrificing quality. Billing required for usage-based charges.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col items-center justify-center gap-2 mb-8"
          variants={itemVariants}
        >
          <div className="flex flex-col sm:flex-row items-start justify-center gap-4">
            <Button
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 px-8 py-6 font-semibold"
              onClick={handleDemoClick}
            >
              Try Interactive Demo →
            </Button>
            <div className="flex flex-col items-center gap-1">
              <Button
                size="lg"
                className="bg-primary text-white hover:bg-primary/90 px-8 py-6 font-semibold opacity-50 cursor-not-allowed"
                disabled
              >
                Sign Up Free
              </Button>
              <span className="text-xs opacity-70">Coming soon...</span>
            </div>
            <Button
              size="lg"
              className="bg-accent text-white hover:bg-accent/90 px-8 py-6 font-semibold"
              onClick={handleTalkToSalesClick}
            >
              Talk to Sales
            </Button>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-6 text-sm opacity-80"
          variants={itemVariants}
        >
          {benefits.map((benefit) => (
            <div key={benefit} className="flex items-center space-x-2">
              <Check className="w-4 h-4" />
              <span>{benefit}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
